import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Home, Printer, CheckCircle2, AlertTriangle, Circle, Sparkles, MessageCircle, Zap, Target, Eye, QrCode, HelpCircle, ClipboardCheck, Award, GraduationCap, BookOpen, Wrench, Lightbulb } from 'lucide-react';
import { Button } from '../../components/ui/button';
import useSeo from '../../hooks/useSeo';
import useLivretSync from '../../hooks/useLivretSync';
import { ProjectorToggle } from '../../components/ProjectorToggle';
import LivretQrShareModal from '../../components/livrets/LivretQrShareModal';
import { toast } from 'sonner';
import { LIVRET_META, SEANCES, COMPETENCES, QCM, QCM_ANALYSE, SITUATIONS_PROBLEMES, BILAN_JE_SAIS, REPARTITION_EXACTE, BILAN_ITEMS, REPERES_QUADRI, DEFIS, THEORIE_SALLE, BIBLIOTHEQUE, QUESTIONS_CLES, PROGRESSION_3H, OUTILS_FORMATEUR, NIVEAUX_ACQUISITION, EVAL_FINALE_GRILLE, EVAL_FINALE_COLS, SITUATIONS_PROBLEMES_EXTRA } from '../../data/livrets/voiturette8h';

const PAGES_BASE = [
  { id: 'home', label: 'Accueil', icon: Home },
  { id: 's1', label: 'Séance 1', icon: Zap },
  { id: 's2', label: 'Séance 2', icon: Target },
  { id: 's3', label: 'Séance 3', icon: Eye },
  { id: 's4', label: 'Séance 4', icon: Award },
  { id: 'biblio', label: 'Bibliothèque', icon: BookOpen },
  { id: 'competences', label: 'Compétences', icon: ClipboardCheck },
  { id: 'qcm', label: 'QCM', icon: HelpCircle },
  { id: 'bilan', label: 'Bilan', icon: GraduationCap },
];
const PAGE_OUTILS = { id: 'outils', label: 'Outils Formateur', icon: Wrench };

export default function LivretVoiturette8h() {
  const [params, setParams] = useSearchParams();
  const { state, save } = useLivretSync(LIVRET_META.key);
  const [qrOpen, setQrOpen] = useState(false);
  // Mode Formateur : révèle réponses attendues + logique de sécurité dans les défis
  const [trainerMode, setTrainerMode] = useState(() => {
    try { return localStorage.getItem('c2r:trainer-mode:voiturette-8h') === '1'; } catch (_) { return false; }
  });
  const toggleTrainer = () => {
    const next = !trainerMode;
    setTrainerMode(next);
    try { localStorage.setItem('c2r:trainer-mode:voiturette-8h', next ? '1' : '0'); } catch (_) {}
    toast.info(next ? '👨‍🏫 Mode Formateur activé — corrections visibles' : '🎓 Mode Élève activé');
  };
  const activePage = params.get('p') || 'home';
  const goPage = (p) => { const n = new URLSearchParams(params); n.set('p', p); setParams(n); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  useSeo({ title: `${LIVRET_META.title} — Code 2 Roues`, description: LIVRET_META.tagline });

  // Onglet Outils Formateur uniquement visible en mode formateur
  const PAGES = useMemo(() => (trainerMode ? [...PAGES_BASE, PAGE_OUTILS] : PAGES_BASE), [trainerMode]);
  // Si mode Élève et l'utilisateur est sur "outils", rediriger vers home
  React.useEffect(() => {
    if (!trainerMode && activePage === 'outils') {
      const n = new URLSearchParams(params); n.set('p', 'home'); setParams(n);
    }
  }, [trainerMode, activePage]);

  const stats = useMemo(() => {
    const totalTasks = SEANCES.reduce((sum, s) => sum + s.themes.length, 0);
    const doneTasks = SEANCES.reduce((sum, s) => sum + s.themes.filter((_, i) => state[`t-${s.id}-${i}`]).length, 0);
    const validSeances = SEANCES.filter((s) => state[`v-${s.id}`]).length;
    const compsAcquis = COMPETENCES.filter((_, i) => state[`c-${i}`] === 'acquis').length;
    const qcmScore = QCM.filter((_, i) => state[`q-${i}`] === QCM[i].a).length;
    const bilanDone = BILAN_ITEMS.filter((_, i) => state[`b-${i}`]).length;
    const total = totalTasks + SEANCES.length + COMPETENCES.length + QCM.length + BILAN_ITEMS.length;
    const done = doneTasks + validSeances + compsAcquis + qcmScore + bilanDone;
    return { pct: total ? Math.round((done / total) * 100) : 0, qcmScore, done, total, validSeances, compsAcquis };
  }, [state]);

  const toggle = (key) => save({ [key]: !state[key] });
  const setC = (key, val) => save({ [key]: val });

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white pb-24">
      {/* Sticky topbar */}
      <div className="sticky top-0 z-40 bg-[#0a0a0b]/85 backdrop-blur border-b border-white/10 print:hidden">
        <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center gap-2 flex-wrap">
          <Button asChild variant="ghost" size="sm" className="text-white/80">
            <Link to="/permis-am/voiturette"><ArrowLeft className="w-4 h-4 mr-1" /> Voiturette</Link>
          </Button>
          <div className="flex-1 min-w-0 hidden sm:block">
            <div className="text-[10px] uppercase tracking-widest text-rose-400 font-black">Livret interactif · 8h</div>
            <div className="text-[13px] text-white/85 font-bold truncate">{stats.pct}% · {stats.done}/{stats.total}</div>
          </div>
          <ProjectorToggle />
          <Button size="sm" onClick={toggleTrainer}
            className={trainerMode ? 'bg-amber-500 hover:bg-amber-400 text-black font-black uppercase tracking-wider' : 'bg-white/10 hover:bg-white/20 text-white/80 font-bold uppercase tracking-wider'}
            data-testid="btn-trainer-mode" title="Bascule Mode Formateur ↔ Mode Élève">
            <GraduationCap className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">{trainerMode ? '👨‍🏫 Formateur' : '🎓 Élève'}</span>
          </Button>
          <Button size="sm" onClick={() => setQrOpen(true)} className="bg-rose-500 hover:bg-rose-400 text-white font-black uppercase tracking-wider" data-testid="btn-qr-share">
            <QrCode className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">QR élève</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} className="hidden sm:inline-flex" data-testid="btn-print">
            <Printer className="w-4 h-4 mr-1" /> Imprimer
          </Button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-white/[0.05] max-w-5xl mx-auto">
          <div className="h-full bg-gradient-to-r from-rose-500 via-amber-400 to-emerald-500 transition-all" style={{ width: `${stats.pct}%` }} />
        </div>

        {/* Nav tabs */}
        <nav className="max-w-5xl mx-auto px-4 py-2 flex gap-1.5 overflow-x-auto no-scrollbar" role="tablist">
          {PAGES.map((p) => {
            const Icon = p.icon;
            const active = p.id === activePage;
            return (
              <button key={p.id} onClick={() => goPage(p.id)} role="tab" aria-selected={active}
                className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11px] font-black uppercase tracking-widest transition border ${active ? 'bg-rose-500 border-rose-500 text-white shadow-lg' : 'bg-white/[0.03] border-white/10 text-white/70 hover:bg-white/[0.06]'}`}
                data-testid={`nav-${p.id}`}>
                <Icon className="w-3.5 h-3.5" />{p.label}
              </button>
            );
          })}
        </nav>
      </div>

      <article className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
        {/* Hero */}
        <header className="mb-6 print:mb-4">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-rose-400 mb-1.5 flex items-center gap-2 flex-wrap">
            <span>🎯 Mission 8H · AM Quadricycle · Topolino</span>
            {trainerMode && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-[9px] font-black uppercase tracking-widest">
                👨‍🏫 Formateur
              </span>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Mon parcours vers l'autonomie</h1>
          <p className="text-sm text-white/60 mt-1">{LIVRET_META.tagline}</p>
        </header>

        {activePage === 'home' && <HomePage />}
        {['s1','s2','s3','s4'].includes(activePage) && (
          <SeancePage seance={SEANCES.find((s) => s.id === activePage)} state={state} toggle={toggle} save={save} trainerMode={trainerMode} />
        )}
        {activePage === 'biblio' && <BibliothequePage state={state} save={save} trainerMode={trainerMode} />}
        {activePage === 'competences' && <CompetencesPage state={state} setC={setC} />}
        {activePage === 'qcm' && <QcmPage state={state} save={save} score={stats.qcmScore} trainerMode={trainerMode} />}
        {activePage === 'bilan' && <BilanPage state={state} toggle={toggle} save={save} stats={stats} />}
        {activePage === 'outils' && trainerMode && <OutilsFormateurPage state={state} save={save} />}

        {/* Footer */}
        <div className="mt-12 text-[10px] text-white/40 text-center print:text-black">
          © Code 2 Roues · Livret pédagogique Voiturette 8h · Version 1 · {new Date().getFullYear()}
        </div>
      </article>

      <LivretQrShareModal open={qrOpen} onClose={() => setQrOpen(false)} livretPath="/livrets/voiturette-8h"
        livretTitle={LIVRET_META.title} livretSubtitle={LIVRET_META.subtitle} accentColor="#f43f5e" tabletMode bypassAuth />
    </div>
  );
}

/* ─── Sub-pages ─── */

function HomePage() {
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL || ''}/api/livrets/am-8h-2026/pdf?dl=1`;
  return (
    <>
      {/* CTA téléchargement PDF · Accessible visiteurs */}
      <section className="mb-6 p-5 rounded-2xl border-2 border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.08] via-emerald-500/[0.03] to-transparent" data-testid="cta-download-pdf">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-400 mb-1">📕 Livret PDF · Édition 2026</div>
            <h2 className="text-lg font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Le livret officiel Code 2 Roues (68 pages)</h2>
            <p className="text-[12.5px] text-white/70 mt-1 leading-snug">Accessible gratuitement — <strong className="text-emerald-300">même sans compte</strong>. Flipbook interactif ou PDF.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
            <Link to="/livret-am/lecteur"
              className="inline-flex items-center justify-center gap-2 px-4 h-11 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-black text-[13px] uppercase tracking-widest shadow-lg transition whitespace-nowrap"
              data-testid="btn-read-pdf-livret-am-home">
              <BookOpen className="w-4 h-4" /> Lire en ligne
            </Link>
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-4 h-11 rounded-xl bg-black/40 border border-emerald-500/40 hover:bg-black/60 text-emerald-300 font-black text-[13px] uppercase tracking-widest shadow-lg transition whitespace-nowrap"
              data-testid="btn-download-pdf-livret-am-home">
              <Printer className="w-4 h-4" /> PDF
            </a>
          </div>
        </div>
      </section>

      <section className="mb-6 p-5 sm:p-6 rounded-2xl border-2 border-rose-500/25 bg-gradient-to-br from-rose-500/10 via-rose-500/5 to-transparent">
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-rose-400 mb-1">Architecture de formation</div>
        <h2 className="text-xl sm:text-2xl font-black text-white leading-tight mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>8 heures · 5 séquences réglementaires · 4 séances pédagogiques</h2>
        <p className="text-[13px] text-white/70 leading-relaxed mb-4">La formation officielle comporte 5 séquences (S1 à S5). Nous les regroupons en 4 séances pédagogiques progressives pour installer les bons réflexes.</p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {SEANCES.map((s, i) => (
            <div key={s.id} className="p-3 rounded-xl border border-white/10 bg-black/25">
              <div className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-1">Séance {i+1} · {s.reg.split('·')[0].trim()}</div>
              <div className="text-[13px] font-black text-white leading-snug">{s.title.split('—')[1]?.trim() || s.title}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Répartition exacte 8h · arrêté 08/11/2012 */}
      <section className="mb-6 p-5 rounded-2xl border border-white/10 bg-white/[0.02]" data-testid="repartition-exacte">
        <div className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-1">Répartition exacte · Arrêté 08/11/2012</div>
        <h3 className="text-lg font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Le détail des 4 × 2 heures</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-white/60 text-[10.5px] font-black uppercase tracking-widest border-b border-white/10">
                <th className="py-2 pr-3">Séance</th><th className="py-2 pr-3">Durée</th><th className="py-2">Contenu</th>
              </tr>
            </thead>
            <tbody>
              {REPARTITION_EXACTE.map((r) => (
                <tr key={r.seance} className="border-b border-white/5">
                  <td className="py-2 pr-3 text-white/85 font-black">Séance {r.seance}</td>
                  <td className="py-2 pr-3"><span className="inline-block px-2 py-0.5 rounded-full bg-rose-500/20 border border-rose-500/40 text-rose-200 text-[11px] font-black">{r.duree}</span></td>
                  <td className="py-2 text-white/75">{r.contenu}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="grid gap-4 sm:grid-cols-2 mb-6">
        <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
          <div className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-1">Fil conducteur</div>
          <div className="text-lg font-black text-white mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>VOIR → ANALYSER → DÉCIDER → AGIR</div>
          <p className="text-[13px] text-white/65 leading-relaxed">Chaque geste est précédé d'une prise d'information et d'une décision.</p>
          <div className="mt-3 p-3 rounded-lg bg-rose-500/10 border border-rose-500/25 text-[12px] text-white/85">
            <strong className="text-rose-300">Principe formateur :</strong> ne pas corriger uniquement le geste, mais faire verbaliser le raisonnement qui l'a produit.
          </div>
        </div>
        <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
          <div className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-1">Cycle pédagogique</div>
          <div className="text-[15px] font-black text-white mb-2 leading-snug" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>EXPLIQUER → MONTRER → FAIRE → OBSERVER → CORRIGER → REFAIRE → VALIDER</div>
          <p className="text-[13px] text-white/65 leading-relaxed">7 temps pour ancrer une compétence.</p>
          <div className="mt-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/25 text-[12px] text-white/85">
            <strong className="text-emerald-300">Objectif final :</strong> rendre l'élève capable de reproduire le comportement sans rappel systématique.
          </div>
        </div>
      </div>

      <section className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
        <div className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-1">Spécificités quadricycle</div>
        <h3 className="text-lg font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>6 repères à installer d'entrée</h3>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {REPERES_QUADRI.map((r) => (
            <div key={r.title} className="p-3 rounded-xl border border-white/10 bg-black/25">
              <div className="text-[11px] font-black uppercase tracking-widest text-rose-300 mb-1">{r.title}</div>
              <div className="text-[12.5px] text-white/75 leading-snug">{r.text}</div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function SeancePage({ seance, state, toggle, save, trainerMode }) {
  if (!seance) return null;
  const valid = Boolean(state[`v-${seance.id}`]);
  const defi = DEFIS[seance.id];
  const defiChoice = state[`defi-${seance.id}`];
  const defiAnswered = defiChoice !== undefined && defiChoice !== null;
  const theorie = THEORIE_SALLE[seance.id] || [];
  return (
    <>
      <section className="mb-6 p-5 sm:p-6 rounded-2xl border-2 border-rose-500/30 bg-gradient-to-br from-rose-500/10 via-rose-500/5 to-transparent">
        <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-rose-400">{seance.reg}</div>
          {seance.badge && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-[9.5px] font-black uppercase tracking-widest" data-testid={`badge-${seance.id}`}>
              🏅 {seance.badge}
            </span>
          )}
        </div>
        <h2 className="text-2xl sm:text-3xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{seance.title}</h2>
        <p className="text-[14px] text-white/70 leading-relaxed mt-2">{seance.goal}</p>
        <div className="mt-3 p-3 rounded-lg bg-white/[0.03] border border-white/10 text-[12.5px] text-white/85">
          <strong className="text-rose-300">Questionnement permanent :</strong> Pourquoi ? Comment ? Quel risque ? Comment l'éviter ?
        </div>
      </section>

      {/* ─── Théorie salle · Élève/Formateur ─── */}
      {theorie.length > 0 && (
        <section className="mb-6" data-testid={`theorie-${seance.id}`}>
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-rose-500/15 border border-rose-500/30">
              <Lightbulb className="w-4 h-4 text-rose-300" />
            </span>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-rose-400">Théorie salle</div>
              <h3 className="text-lg font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Avant la pratique — ce qu'on installe en salle</h3>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {theorie.map((t, i) => {
              const readKey = `th-${seance.id}-${i}`;
              const read = Boolean(state[readKey]);
              return (
                <div key={i} className={`p-4 rounded-xl border transition ${read ? 'border-emerald-500/40 bg-emerald-500/[0.04]' : 'border-white/10 bg-white/[0.02] hover:border-white/25'}`} data-testid={`theorie-${seance.id}-${i}`}>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl leading-none flex-shrink-0" aria-hidden>{t.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-black text-white leading-snug">{t.title}</div>
                      <p className="text-[12.5px] text-white/70 mt-1 leading-relaxed">{t.student}</p>
                      {trainerMode && (
                        <div className="mt-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/40" data-testid={`theorie-trainer-${seance.id}-${i}`}>
                          <div className="text-[9.5px] font-black uppercase tracking-widest text-amber-300 mb-1">👨‍🏫 Repère formateur</div>
                          <p className="text-[12px] text-white/85 leading-snug">{t.trainer}</p>
                        </div>
                      )}
                      <label className="mt-2 flex items-center gap-2 text-[10.5px] font-black uppercase tracking-widest cursor-pointer">
                        <input type="checkbox" checked={read} onChange={() => save({ [readKey]: !read })} className="w-3.5 h-3.5 accent-emerald-500" />
                        <span className={read ? 'text-emerald-400' : 'text-white/45'}>Vu en salle</span>
                      </label>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Fiches pédagogiques */}
      <section className="mb-6">
        <h3 className="text-lg font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Fiches pédagogiques</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          {seance.themes.map(([title, desc], i) => {
            const done = Boolean(state[`t-${seance.id}-${i}`]);
            return (
              <label key={i} className={`p-4 rounded-xl border cursor-pointer transition ${done ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-white/10 bg-white/[0.02] hover:border-white/25'}`}
                data-testid={`theme-${seance.id}-${i}`}>
                <div className="flex items-start gap-3">
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full text-[10px] font-black flex items-center justify-center ${done ? 'bg-emerald-500 text-white' : 'bg-rose-500/20 text-rose-300 border border-rose-500/40'}`}>{String(i+1).padStart(2,'0')}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-black text-white leading-snug">{title}</div>
                    <div className="text-[12.5px] text-white/60 mt-0.5 leading-snug">{desc}</div>
                    <div className="mt-2 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest">
                      <input type="checkbox" checked={done} onChange={() => toggle(`t-${seance.id}-${i}`)} className="w-4 h-4 accent-emerald-500 cursor-pointer" />
                      <span className={done ? 'text-emerald-400' : 'text-white/50'}>Compétence travaillée</span>
                    </div>
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      </section>

      <div className="grid gap-4 sm:grid-cols-2 mb-6">
        <div className="p-4 rounded-2xl border border-white/10 bg-white/[0.02]">
          <div className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-1">Mise en situation</div>
          <h4 className="text-[15px] font-black text-white mb-2">Exercice terrain</h4>
          <p className="text-[13px] text-white/70 leading-relaxed">{seance.exercise}</p>
          <div className="mt-2 p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/25 text-[11.5px] text-white/85">
            <strong className="text-emerald-300">Critères d'observation :</strong> prise d'info · allure · trajectoire · décision · maîtrise · verbalisation.
          </div>
        </div>
        <div className="p-4 rounded-2xl border border-white/10 bg-white/[0.02]">
          <div className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-1">Risques à faire émerger</div>
          <div className="mb-2 p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/25 text-[12px] text-white/85">
            <strong className="text-rose-300">Ce que l'élève doit comprendre :</strong> {seance.risk}
          </div>
          <div className="text-[10px] font-black uppercase tracking-widest text-rose-400 mt-3 mb-1">Questions enseignant</div>
          <ul className="space-y-1">
            {seance.questions.map((q, i) => (
              <li key={i} className="flex items-start gap-2 text-[12.5px] text-white/75 leading-snug">
                <MessageCircle className="w-3.5 h-3.5 text-rose-400 flex-shrink-0 mt-0.5" /><span>{q}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Défi de la mission — scénario interactif */}
      {defi && (
        <section className="mb-6 p-5 rounded-2xl border-2 border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent" data-testid={`defi-${seance.id}`}>
          <div className="text-[10px] font-black uppercase tracking-widest text-amber-400 mb-1">🎯 Défi de la mission</div>
          <h4 className="text-lg font-black text-white mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{defi.title}</h4>
          <p className="text-[13.5px] text-white/85 mb-4 leading-relaxed">{defi.scenario}</p>
          <div className="grid gap-2 sm:grid-cols-3">
            {defi.options.map((opt, j) => {
              const isChosen = defiChoice === j;
              let cls = 'border-white/15 bg-white/[0.02] text-white/85 hover:border-amber-500/50';
              if (defiAnswered && isChosen && opt.right) cls = 'border-emerald-500 bg-emerald-500/20 text-white';
              else if (defiAnswered && isChosen && !opt.right) cls = 'border-rose-500 bg-rose-500/20 text-white';
              else if (defiAnswered && opt.right) cls = 'border-emerald-500/50 bg-emerald-500/10 text-emerald-200';
              return (
                <button key={j} onClick={() => save({ [`defi-${seance.id}`]: j })} disabled={defiAnswered}
                  className={`text-left px-3 py-2.5 rounded-lg border-2 text-[12.5px] font-semibold transition ${cls}`}
                  data-testid={`defi-${seance.id}-${j}`}>
                  <span className="inline-block w-5 text-amber-300 font-black">{String.fromCharCode(65+j)}.</span> {opt.text}
                </button>
              );
            })}
          </div>
          {defiAnswered && (
            <div className={`mt-3 p-2.5 rounded-lg text-[12.5px] ${defi.options[defiChoice].right ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-200' : 'bg-rose-500/10 border border-rose-500/30 text-rose-200'}`}>
              {defi.options[defiChoice].fb}
            </div>
          )}
          {/* Mode Formateur : logique de sécurité révélée */}
          {trainerMode && (
            <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border-2 border-amber-500/40" data-testid={`defi-logic-${seance.id}`}>
              <div className="text-[10px] font-black uppercase tracking-widest text-amber-400 mb-1">👨‍🏫 Réponse attendue · logique de sécurité (Mode Formateur)</div>
              <div className="text-[12.5px] text-white/90 leading-relaxed">{defi.logic}</div>
            </div>
          )}
        </section>
      )}

      {/* Trace pédagogique */}
      <section className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
        <div className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-1">Trace pédagogique · V8</div>
        <h4 className="text-[15px] font-black text-white mb-3">Bilan de séance — niveau, réussites, correction, objectif</h4>

        {/* Niveau d'acquisition */}
        <div className="mb-3">
          <div className="text-[10px] font-black uppercase tracking-widest text-white/60 mb-1.5">Niveau d'acquisition</div>
          <div className="flex flex-wrap gap-1.5">
            {NIVEAUX_ACQUISITION.map((n) => {
              const active = state[`niv-${seance.id}`] === n.key;
              return (
                <button key={n.key} type="button" onClick={() => save({ [`niv-${seance.id}`]: active ? null : n.key })}
                  className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11px] font-black uppercase tracking-widest border-2 transition"
                  style={{ borderColor: active ? n.color : `${n.color}55`, background: active ? `${n.color}22` : 'transparent', color: active ? '#fff' : `${n.color}` }}
                  data-testid={`niv-${seance.id}-${n.key}`}>
                  <span>{n.emoji}</span>{n.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Formulaire trace pédagogique */}
        <div className="grid gap-3 sm:grid-cols-2 mb-3">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-emerald-400 block mb-1">Réussites observées</label>
            <textarea value={state[`trace-reussite-${seance.id}`] || ''} onChange={(e) => save({ [`trace-reussite-${seance.id}`]: e.target.value })} rows={3}
              placeholder="Ce qui a fonctionné, ce qui est stabilisé…"
              className="w-full rounded-lg bg-black/40 border border-white/15 focus:border-emerald-500 outline-none px-3 py-2 text-white text-[12.5px]"
              data-testid={`trace-reussite-${seance.id}`} />
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-rose-400 block mb-1">Difficultés / erreurs observées</label>
            <textarea value={state[`trace-difficulte-${seance.id}`] || ''} onChange={(e) => save({ [`trace-difficulte-${seance.id}`]: e.target.value })} rows={3}
              placeholder="Ce qui a coincé, ce qui est resté fragile…"
              className="w-full rounded-lg bg-black/40 border border-white/15 focus:border-rose-500 outline-none px-3 py-2 text-white text-[12.5px]"
              data-testid={`trace-difficulte-${seance.id}`} />
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-amber-400 block mb-1">Correction / remédiation</label>
            <textarea value={state[`trace-correction-${seance.id}`] || ''} onChange={(e) => save({ [`trace-correction-${seance.id}`]: e.target.value })} rows={3}
              placeholder="Ce qu'on a corrigé et la méthode retenue…"
              className="w-full rounded-lg bg-black/40 border border-white/15 focus:border-amber-500 outline-none px-3 py-2 text-white text-[12.5px]"
              data-testid={`trace-correction-${seance.id}`} />
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-sky-400 block mb-1">Objectif pour la suite</label>
            <textarea value={state[`trace-objectif-${seance.id}`] || ''} onChange={(e) => save({ [`trace-objectif-${seance.id}`]: e.target.value })} rows={3}
              placeholder="Ce sur quoi je vais travailler la prochaine fois…"
              className="w-full rounded-lg bg-black/40 border border-white/15 focus:border-sky-500 outline-none px-3 py-2 text-white text-[12.5px]"
              data-testid={`trace-objectif-${seance.id}`} />
          </div>
        </div>

        {/* Bilan libre + validation */}
        <label className="text-[10px] font-black uppercase tracking-widest text-white/60 block mb-1">Bilan libre</label>
        <textarea value={state[`bilan-${seance.id}`] || ''} onChange={(e) => save({ [`bilan-${seance.id}`]: e.target.value })}
          rows={3} placeholder="Réussites · erreurs · corrections · stratégie à reprendre…"
          className="w-full rounded-lg bg-black/40 border border-white/15 focus:border-rose-500 outline-none px-3 py-2 text-white text-[13px] leading-relaxed"
          data-testid={`bilan-${seance.id}`} />
        <div className="mt-3 flex items-center gap-3">
          <Button onClick={() => toggle(`v-${seance.id}`)}
            className={valid ? 'bg-emerald-500 hover:bg-emerald-400 text-white font-black' : 'bg-rose-500 hover:bg-rose-400 text-white font-black'}
            data-testid={`valider-${seance.id}`}>
            {valid ? <><CheckCircle2 className="w-4 h-4 mr-1" /> Séance validée</> : <>Valider la séance</>}
          </Button>
          <span className={`text-[11px] font-black uppercase tracking-widest ${valid ? 'text-emerald-400' : 'text-white/50'}`}>
            {valid ? '✓ Validée' : 'Non validée'}
          </span>
        </div>
      </section>
    </>
  );
}

function CompetencesPage({ state, setC }) {
  return (
    <>
      <section className="mb-6 p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
        <div className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-1">Référentiel de progression</div>
        <h2 className="text-2xl font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Grille de compétences</h2>
        <p className="text-[12.5px] text-white/60 mb-4">Coche pour chaque critère observable la case correspondant au niveau atteint.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[10px] font-black uppercase tracking-widest text-white/50 border-b border-white/10">
                <th className="py-2 pr-3">Domaine</th>
                <th className="py-2 pr-3">Critère observable</th>
                <th className="py-2 pr-3 w-20 text-center">Acquis</th>
                <th className="py-2 pr-3 w-24 text-center">À renforcer</th>
                <th className="py-2 pr-3 w-24 text-center">Non acquis</th>
              </tr>
            </thead>
            <tbody>
              {COMPETENCES.map(([domain, crit], i) => {
                const val = state[`c-${i}`];
                return (
                  <tr key={i} className="border-b border-white/[0.06]">
                    <td className="py-2 pr-3 text-rose-300 font-black text-[11.5px] uppercase tracking-wide">{domain}</td>
                    <td className="py-2 pr-3 text-white/85">{crit}</td>
                    {['acquis','renforcer','non-acquis'].map((v) => {
                      const active = val === v;
                      const color = v === 'acquis' ? 'emerald' : v === 'renforcer' ? 'amber' : 'rose';
                      return (
                        <td key={v} className="py-2 pr-3 text-center">
                          <button type="button" onClick={() => setC(`c-${i}`, active ? null : v)}
                            className={`w-6 h-6 rounded-full border-2 inline-flex items-center justify-center transition ${active ? `bg-${color}-500 border-${color}-500` : `border-${color}-500/40 hover:border-${color}-500`}`}
                            style={active ? { background: v === 'acquis' ? '#10b981' : v === 'renforcer' ? '#f59e0b' : '#e11d48', borderColor: v === 'acquis' ? '#10b981' : v === 'renforcer' ? '#f59e0b' : '#e11d48' } : { borderColor: v === 'acquis' ? '#10b98155' : v === 'renforcer' ? '#f59e0b55' : '#e11d4855' }}
                            data-testid={`comp-${i}-${v}`} aria-pressed={active}>
                            {active && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      <section className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
        <h3 className="text-[15px] font-black text-white mb-3">Échelle de validation</h3>
        <div className="grid gap-2 sm:grid-cols-3">
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30"><div className="text-[10px] font-black uppercase tracking-widest text-emerald-400 mb-1">1 · Acquis</div><div className="text-[12px] text-white/80">Réalisé avec autonomie et compréhension.</div></div>
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30"><div className="text-[10px] font-black uppercase tracking-widest text-amber-400 mb-1">2 · À renforcer</div><div className="text-[12px] text-white/80">Réalisé avec aide, hésitation ou erreur corrigée.</div></div>
          <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/30"><div className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-1">3 · Non acquis</div><div className="text-[12px] text-white/80">Risque ou maîtrise insuffisante — à retravailler.</div></div>
        </div>
      </section>
    </>
  );
}

/* ══════════════════════ Bibliothèque pédagogique ══════════════════════ */
function BibliothequePage({ state, save, trainerMode }) {
  const totalRead = BIBLIOTHEQUE.filter((_, i) => state[`bib-${i}`]).length;
  return (
    <>
      <section className="mb-6 p-5 sm:p-6 rounded-2xl border-2 border-rose-500/25 bg-gradient-to-br from-rose-500/10 via-rose-500/5 to-transparent">
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-rose-400 mb-1 flex items-center gap-2 flex-wrap">
          <BookOpen className="w-4 h-4" /><span>Bibliothèque pédagogique</span>
          <span className="ml-auto inline-block px-2 py-0.5 rounded-full bg-white/10 text-white/80 text-[10px] font-black">{totalRead}/{BIBLIOTHEQUE.length} thèmes vus</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>10 thèmes clés pour raisonner sa conduite</h2>
        <p className="text-[13px] text-white/70 leading-relaxed mt-2">Chaque thème est structuré en 4 temps : <strong className="text-rose-300">Apport salle</strong> → <strong className="text-rose-300">Activité</strong> → <strong className="text-rose-300">Risque / erreur</strong> → <strong className="text-rose-300">Validation</strong>.</p>
      </section>

      {/* 10 thèmes */}
      <section className="mb-6 grid gap-3 sm:grid-cols-2">
        {BIBLIOTHEQUE.map((b, i) => {
          const read = Boolean(state[`bib-${i}`]);
          return (
            <div key={i} className={`p-4 rounded-2xl border transition ${read ? 'border-emerald-500/40 bg-emerald-500/[0.04]' : 'border-white/10 bg-white/[0.02] hover:border-white/25'}`} data-testid={`biblio-${i}`}>
              <div className="flex items-start gap-3 mb-2">
                <span className="text-2xl leading-none flex-shrink-0" aria-hidden>{b.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-black uppercase tracking-widest text-rose-400">Thème {String(i+1).padStart(2,'0')}</div>
                  <div className="text-[14px] font-black text-white leading-snug">{b.title}</div>
                </div>
              </div>
              <div className="space-y-2 text-[12.5px]">
                <div><span className="text-[10px] font-black uppercase tracking-widest text-emerald-400">🎓 Apport salle · </span><span className="text-white/80">{b.apport}</span></div>
                <div><span className="text-[10px] font-black uppercase tracking-widest text-amber-400">⚡ Activité · </span><span className="text-white/80">{b.activite}</span></div>
                <div><span className="text-[10px] font-black uppercase tracking-widest text-rose-400">🚨 Risque · </span><span className="text-white/80">{b.risque}</span></div>
                <div><span className="text-[10px] font-black uppercase tracking-widest text-sky-400">✅ Validation · </span><span className="text-white/80">{b.validation}</span></div>
              </div>
              <label className="mt-3 flex items-center gap-2 text-[10.5px] font-black uppercase tracking-widest cursor-pointer">
                <input type="checkbox" checked={read} onChange={() => save({ [`bib-${i}`]: !read })} className="w-3.5 h-3.5 accent-emerald-500" />
                <span className={read ? 'text-emerald-400' : 'text-white/50'}>Thème vu</span>
              </label>
            </div>
          );
        })}
      </section>

      {/* 10 questions clés */}
      <section className="mb-6 p-5 rounded-2xl border border-white/10 bg-white/[0.02]" data-testid="questions-cles">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/30">
            <MessageCircle className="w-4 h-4 text-amber-300" />
          </span>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-amber-400">Banque de réflexion</div>
            <h3 className="text-lg font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>10 questions clés — à faire émerger</h3>
          </div>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          {QUESTIONS_CLES.map((q, i) => (
            <div key={i} className="p-3 rounded-lg bg-black/25 border border-white/10 text-[12.5px] text-white/85 leading-snug flex items-start gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-[10px] font-black flex items-center justify-center">{i+1}</span>
              <span>{q}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Progression 3h hors circulation */}
      <section className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]" data-testid="progression-3h">
        <div className="text-[10px] font-black uppercase tracking-widest text-emerald-400 mb-1">Progression atelier salle</div>
        <h3 className="text-lg font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>3 h hors circulation — blocs recommandés</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-white/60 text-[10px] font-black uppercase tracking-widest border-b border-white/10">
                <th className="py-2 pr-3">Bloc</th><th className="py-2 pr-3">Durée</th><th className="py-2 pr-3">Compétence dominante</th><th className="py-2">Validation</th>
              </tr>
            </thead>
            <tbody>
              {PROGRESSION_3H.map((p, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="py-2 pr-3 text-white/85 font-black">{p.bloc}</td>
                  <td className="py-2 pr-3"><span className="inline-block px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 text-[10.5px] font-black">{p.duree}</span></td>
                  <td className="py-2 pr-3 text-white/75">{p.competence}</td>
                  <td className="py-2 text-white/75">{p.validation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

/* ══════════════════════ Outils Formateur ══════════════════════ */
function OutilsFormateurPage({ state, save }) {
  const [tab, setTab] = React.useState('brief');
  const TABS = [
    { id: 'brief', label: '📋 Brief', icon: ClipboardCheck },
    { id: 'debrief', label: '🔄 Débrief 4 temps', icon: MessageCircle },
    { id: 'fiche', label: '👁️ Fiche observation', icon: Eye },
    { id: 'remediation', label: '🛠️ Remédiation 5 étapes', icon: Wrench },
    { id: 'defis', label: '🎯 12 défis', icon: Target },
    { id: 'script', label: '📝 Script débrief 5 min', icon: Sparkles },
    { id: 'banque', label: '💬 Banque de relances', icon: MessageCircle },
    { id: 'trame', label: '🎓 Trame formateur/élève', icon: GraduationCap },
    { id: 'glossaire', label: '📚 Glossaire', icon: BookOpen },
  ];
  return (
    <>
      <section className="mb-6 p-5 sm:p-6 rounded-2xl border-2 border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent">
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-400 mb-1 flex items-center gap-2">
          <Wrench className="w-4 h-4" /><span>Outils formateur · Mode Formateur uniquement</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Ma boîte à outils pédagogique</h2>
        <p className="text-[13px] text-white/70 leading-relaxed mt-2">Briefing, débriefing, observation, remédiation, défis, relances : tout ce dont tu as besoin pour animer une séance pro.</p>
      </section>

      {/* Onglets outils */}
      <div className="mb-5 flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`outil-tab-${t.id}`}
            className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[10.5px] font-black uppercase tracking-widest transition border ${tab === t.id ? 'bg-amber-500 border-amber-500 text-black' : 'bg-white/[0.03] border-white/10 text-white/70 hover:bg-white/[0.06]'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'brief' && (
        <section className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
          <h3 className="text-lg font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Avant la séance — Brief formateur (5 points)</h3>
          <div className="space-y-2">
            {OUTILS_FORMATEUR.brief.map((b, i) => {
              const done = Boolean(state[`brief-${i}`]);
              return (
                <label key={i} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${done ? 'border-emerald-500/40 bg-emerald-500/[0.05]' : 'border-white/10 bg-black/25 hover:border-white/25'}`}>
                  <input type="checkbox" checked={done} onChange={() => save({ [`brief-${i}`]: !done })} className="mt-0.5 w-4 h-4 accent-emerald-500" />
                  <div className="flex-1">
                    <div className="text-[13px] font-black text-white">{b.titre}</div>
                    <div className="text-[12px] text-white/70 mt-0.5">{b.desc}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </section>
      )}

      {tab === 'debrief' && (
        <section className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
          <h3 className="text-lg font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Fin de séance — Débrief en 4 temps</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {OUTILS_FORMATEUR.debrief.map((d) => (
              <div key={d.n} className="p-4 rounded-xl border border-amber-500/25 bg-amber-500/[0.05]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-500 text-black text-[13px] font-black">{d.n}</span>
                  <div className="text-[13px] font-black text-white">{d.temps}</div>
                </div>
                <p className="text-[12.5px] text-white/80 italic mt-1">{d.q}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === 'fiche' && (
        <section className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
          <h3 className="text-lg font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Fiche d'observation — 6 domaines</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-left text-white/60 text-[10px] font-black uppercase tracking-widest border-b border-white/10">
                  <th className="py-2 pr-3">Domaine</th><th className="py-2 pr-3">À observer</th><th className="py-2 pr-3">Question de relance</th><th className="py-2 text-center w-32">Décision</th>
                </tr>
              </thead>
              <tbody>
                {OUTILS_FORMATEUR.fiche_observation.map((f, i) => {
                  const val = state[`fo-${i}`];
                  return (
                    <tr key={i} className="border-b border-white/5">
                      <td className="py-2 pr-3 text-amber-300 font-black text-[11.5px] uppercase tracking-wide">{f.domaine}</td>
                      <td className="py-2 pr-3 text-white/80">{f.observer}</td>
                      <td className="py-2 pr-3 text-white/75 italic">{f.relance}</td>
                      <td className="py-2 text-center">
                        <div className="inline-flex gap-1">
                          {[['🟢','ok','#10b981'],['🟠','wip','#f59e0b'],['🔴','ko','#e11d48']].map(([emoji,key,c]) => (
                            <button key={key} onClick={() => save({ [`fo-${i}`]: val === key ? null : key })}
                              className="w-7 h-7 rounded-full border-2 text-[11px] transition"
                              style={{ borderColor: val === key ? c : `${c}55`, background: val === key ? `${c}33` : 'transparent' }}
                              data-testid={`fo-${i}-${key}`}>{emoji}</button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'remediation' && (
        <section className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
          <h3 className="text-lg font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Méthode de remédiation — 5 étapes</h3>
          <ol className="space-y-2">
            {OUTILS_FORMATEUR.remediation.map((r) => (
              <li key={r.n} className="flex items-start gap-3 p-3 rounded-lg bg-black/25 border border-white/10">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500 text-black text-[13px] font-black flex items-center justify-center">{r.n}</span>
                <div>
                  <div className="text-[13.5px] font-black text-white">{r.mot}</div>
                  <div className="text-[12.5px] text-white/75 mt-0.5">{r.desc}</div>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {tab === 'defis' && (
        <section className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
          <h3 className="text-lg font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>12 défis pédagogiques</h3>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {OUTILS_FORMATEUR.defis.map((d, i) => {
              const done = Boolean(state[`defiPed-${i}`]);
              return (
                <label key={d.n} className={`p-3 rounded-xl border cursor-pointer transition flex items-start gap-2.5 ${done ? 'border-emerald-500/40 bg-emerald-500/[0.05]' : 'border-white/10 bg-black/25 hover:border-white/25'}`}>
                  <input type="checkbox" checked={done} onChange={() => save({ [`defiPed-${i}`]: !done })} className="mt-0.5 w-4 h-4 accent-emerald-500" />
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-amber-400">Défi {d.n}</div>
                    <div className="text-[13px] font-black text-white leading-snug">{d.titre}</div>
                    <div className="text-[11.5px] text-white/70 leading-snug mt-0.5">{d.desc}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </section>
      )}

      {tab === 'script' && (
        <section className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
          <h3 className="text-lg font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Script de débrief — 5 minutes chrono</h3>
          <ol className="space-y-2">
            {OUTILS_FORMATEUR.script_debrief.map((s, i) => (
              <li key={i} className="p-3 rounded-lg bg-black/25 border border-white/10">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-rose-500 text-white text-[12px] font-black flex items-center justify-center">{i+1}</span>
                  <div className="flex-1">
                    <div className="text-[13px] font-black text-rose-300 uppercase tracking-wide">{s.etape}</div>
                    <div className="text-[12.5px] text-white/85 italic mt-1">{s.q}</div>
                    <div className="text-[11.5px] text-white/55 mt-1">→ {s.but}</div>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {tab === 'banque' && (
        <section className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
          <h3 className="text-lg font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Banque de relances — 30 formulations</h3>
          <p className="text-[12px] text-white/60 mb-3">À piocher en séance pour faire émerger l'analyse chez l'élève.</p>
          <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
            {OUTILS_FORMATEUR.banque_relances.map((r, i) => (
              <div key={i} className="p-2.5 rounded-lg bg-black/25 border border-white/10 text-[12px] text-white/80 italic leading-snug">
                {r}
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === 'trame' && (
        <section className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
          <h3 className="text-lg font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Trame d'échange formateur / élève — 7 temps</h3>
          <ol className="space-y-2">
            {OUTILS_FORMATEUR.trame_echange.map((t) => (
              <li key={t.n} className="p-3 rounded-lg bg-black/25 border border-white/10 flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500 text-white text-[13px] font-black flex items-center justify-center">{t.n}</span>
                <div>
                  <div className="text-[13px] font-black text-emerald-300">{t.action}</div>
                  <div className="text-[12.5px] text-white/85 italic mt-1">{t.q}</div>
                  <div className="text-[11.5px] text-white/55 mt-1">→ {t.but}</div>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {tab === 'glossaire' && (
        <section className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
          <h3 className="text-lg font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Mini-glossaire pédagogique</h3>
          <div className="grid gap-2 sm:grid-cols-2">
            {OUTILS_FORMATEUR.glossaire.map((g, i) => (
              <div key={i} className="p-3 rounded-lg bg-black/25 border border-white/10">
                <div className="text-[10px] font-black uppercase tracking-widest text-rose-400">{g.term}</div>
                <div className="text-[12.5px] text-white/80 mt-1 leading-snug">{g.def}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}


function QcmPage({ state, save, score, trainerMode }) {
  const [tab, setTab] = React.useState('base');
  const activeQcm = tab === 'analyse' ? QCM_ANALYSE : QCM;
  const prefix = tab === 'analyse' ? 'qa' : 'q';
  const activeScore = activeQcm.reduce((acc, q, i) => acc + (state[`${prefix}-${i}`] === q.a ? 1 : 0), 0);
  return (
    <section className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-1">Évaluation formative</div>
          <h2 className="text-2xl font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>QCM · {tab === 'analyse' ? 'analyse & décision' : tab === 'situations' ? 'situations-problèmes' : 'compréhension & prévention'}</h2>
        </div>
        {tab !== 'situations' && <div className="text-[13px] text-white/60">Score : <strong className="text-emerald-400 text-lg" data-testid="qcm-score">{activeScore}</strong> / {activeQcm.length}</div>}
      </div>
      {/* Toggle QCM base / analyse / situations */}
      <div className="mb-4 inline-flex rounded-lg border border-white/15 overflow-hidden flex-wrap">
        <button onClick={() => setTab('base')} data-testid="qcm-tab-base"
          className={`px-3 py-1.5 text-[11px] font-black uppercase tracking-widest transition ${tab === 'base' ? 'bg-rose-500 text-white' : 'bg-white/[0.02] text-white/60 hover:bg-white/[0.05]'}`}>
          🎯 Prévention (10)
        </button>
        <button onClick={() => setTab('analyse')} data-testid="qcm-tab-analyse"
          className={`px-3 py-1.5 text-[11px] font-black uppercase tracking-widest transition ${tab === 'analyse' ? 'bg-amber-500 text-black' : 'bg-white/[0.02] text-white/60 hover:bg-white/[0.05]'}`}>
          🧠 Analyse & décision (10)
        </button>
        <button onClick={() => setTab('situations')} data-testid="qcm-tab-situations"
          className={`px-3 py-1.5 text-[11px] font-black uppercase tracking-widest transition ${tab === 'situations' ? 'bg-emerald-500 text-white' : 'bg-white/[0.02] text-white/60 hover:bg-white/[0.05]'}`}>
          🎮 Situations-problèmes (8)
        </button>
      </div>
      {/* Vue "Situations-problèmes" — verbalisation à faire émerger */}
      {tab === 'situations' && (
        <div className="space-y-3" data-testid="situations-list">
          <p className="text-[12.5px] text-white/60 mb-2">Trouve le risque et construis ta décision pour chaque scénario. Verbalise à voix haute.</p>
          {[...SITUATIONS_PROBLEMES, ...SITUATIONS_PROBLEMES_EXTRA].map((s, i) => (
            <div key={i} className="p-4 rounded-xl border border-white/10 bg-black/25" data-testid={`sit-${i}`}>
              <div className="text-[13.5px] font-black text-white mb-1">{s.emoji} {s.title}</div>
              <p className="text-[13px] text-white/75 mb-2"><strong className="text-white/90">Situation :</strong> {s.situation}</p>
              <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/25 text-[12.5px] text-white/85">
                <strong className="text-rose-300">À verbaliser :</strong> {s.verbaliser}
              </div>
              {trainerMode && (
                <div className="mt-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/40 text-[12.5px] text-white/90" data-testid={`sit-intent-${i}`}>
                  <strong className="text-amber-300">👨‍🏫 Intention pédagogique :</strong> {s.intention}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {/* Vue QCM classique */}
      {tab !== 'situations' && (
      <>
      <p className="text-[12.5px] text-white/60 mb-4">Choisis la bonne réponse. Retour immédiat après ton clic.</p>
      <div className="space-y-4">
        {activeQcm.map((q, i) => {
          const chosen = state[`${prefix}-${i}`];
          const answered = chosen !== undefined && chosen !== null;
          return (
            <div key={i} className="p-4 rounded-xl border border-white/10 bg-black/25" data-testid={`qcm-q-${i}`}>
              <div className="text-[13.5px] font-black text-white mb-2">{i+1}. {q.q}</div>
              <div className="grid gap-1.5 sm:grid-cols-3">
                {q.opts.map((opt, j) => {
                  const isChosen = chosen === j;
                  const isRight = j === q.a;
                  let cls = 'border-white/15 bg-white/[0.02] text-white/85 hover:border-white/30';
                  if (answered && isChosen && isRight) cls = 'border-emerald-500 bg-emerald-500/20 text-white';
                  else if (answered && isChosen && !isRight) cls = 'border-rose-500 bg-rose-500/20 text-white';
                  else if (answered && isRight) cls = 'border-emerald-500/50 bg-emerald-500/10 text-emerald-200';
                  return (
                    <button key={j} onClick={() => save({ [`${prefix}-${i}`]: j })} disabled={answered}
                      className={`text-left px-3 py-2 rounded-lg border-2 text-[13px] font-semibold transition ${cls}`}
                      data-testid={`qcm-${i}-${j}`}>
                      <span className="inline-block w-5 text-rose-300 font-black">{String.fromCharCode(65+j)}.</span> {opt}
                    </button>
                  );
                })}
              </div>
              {answered && (
                <div className={`mt-2 text-[12px] ${chosen === q.a ? 'text-emerald-400' : 'text-rose-400'} font-black`}>
                  {chosen === q.a ? '✓ Bonne réponse' : `✗ Bonne réponse : ${q.opts[q.a]}`}
                </div>
              )}
            </div>
          );
        })}
      </div>
      </>
      )}
    </section>
  );
}

function BilanPage({ state, toggle, save, stats }) {
  return (
    <>
      <section className="mb-6 p-5 rounded-2xl border-2 border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent">
        <div className="text-[10px] font-black uppercase tracking-widest text-emerald-400 mb-1">Fin de parcours</div>
        <h2 className="text-2xl font-black text-white mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Bilan élève & enseignant</h2>
        <div className="grid gap-2 sm:grid-cols-4 mb-4">
          <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10 text-center">
            <div className="text-[10px] font-black uppercase tracking-widest text-white/50">Progression</div>
            <div className="text-2xl font-black text-white">{stats.pct}%</div>
          </div>
          <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10 text-center">
            <div className="text-[10px] font-black uppercase tracking-widest text-white/50">Séances</div>
            <div className="text-2xl font-black text-emerald-400">{stats.validSeances}/4</div>
          </div>
          <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10 text-center">
            <div className="text-[10px] font-black uppercase tracking-widest text-white/50">Compétences</div>
            <div className="text-2xl font-black text-emerald-400">{stats.compsAcquis}/{COMPETENCES.length}</div>
          </div>
          <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10 text-center">
            <div className="text-[10px] font-black uppercase tracking-widest text-white/50">QCM</div>
            <div className="text-2xl font-black text-emerald-400">{stats.qcmScore}/{QCM.length}</div>
          </div>
        </div>
      </section>

      <div className="grid gap-4 sm:grid-cols-2 mb-6">
        <div className="p-4 rounded-2xl border border-white/10 bg-white/[0.02]">
          <h3 className="text-[15px] font-black text-white mb-3">Je suis capable de…</h3>
          <div className="space-y-1.5">
            {BILAN_ITEMS.map((item, i) => {
              const done = Boolean(state[`b-${i}`]);
              return (
                <label key={i} className="flex items-start gap-2 text-[13px] text-white/85 cursor-pointer">
                  <input type="checkbox" checked={done} onChange={() => toggle(`b-${i}`)} className="mt-0.5 w-4 h-4 accent-emerald-500 cursor-pointer" data-testid={`bilan-${i}`} />
                  <span className={done ? 'line-through text-white/50' : ''}>{item}</span>
                </label>
              );
            })}
          </div>
        </div>
        <div className="p-4 rounded-2xl border border-white/10 bg-white/[0.02]">
          <h3 className="text-[15px] font-black text-white mb-2">Mes points de vigilance</h3>
          <textarea value={state.vigilance || ''} onChange={(e) => save({ vigilance: e.target.value })} rows={5}
            placeholder="Ce sur quoi je dois particulièrement rester vigilant après ma formation…"
            className="w-full rounded-lg bg-black/40 border border-white/15 focus:border-rose-500 outline-none px-3 py-2 text-white text-[13px] leading-relaxed"
            data-testid="bilan-vigilance" />
        </div>
      </div>

      {/* Nouveau bloc V6 : "Je sais…" + "Mon objectif après la formation" */}
      <section className="mb-6 p-4 rounded-2xl border-2 border-emerald-500/25 bg-emerald-500/[0.05]" data-testid="bilan-je-sais">
        <div className="text-[10px] font-black uppercase tracking-widest text-emerald-400 mb-2">Auto-évaluation V6 · Je sais…</div>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {BILAN_JE_SAIS.map((item, i) => {
            const done = Boolean(state[`js-${i}`]);
            return (
              <label key={i} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition ${done ? 'bg-emerald-500/15 border-emerald-500/40' : 'bg-white/[0.02] border-white/10 hover:border-white/25'}`} data-testid={`js-${i}`}>
                <input type="checkbox" checked={done} onChange={() => save({ [`js-${i}`]: !done })} className="w-4 h-4 accent-emerald-500" />
                <span className={`text-[13px] ${done ? 'text-white/60 line-through' : 'text-white/85'}`}>Je sais {item}.</span>
              </label>
            );
          })}
        </div>
      </section>

      <section className="mb-6 p-4 rounded-2xl border border-white/10 bg-white/[0.02]">
        <h3 className="text-[15px] font-black text-white mb-2">Mon objectif après la formation</h3>
        <textarea value={state.objectifPost || ''} onChange={(e) => save({ objectifPost: e.target.value })} rows={4}
          placeholder="Ce que je veux continuer à surveiller / améliorer…"
          className="w-full rounded-lg bg-black/40 border border-white/15 focus:border-emerald-500 outline-none px-3 py-2 text-white text-[13px] leading-relaxed"
          data-testid="bilan-objectif-post" />
      </section>

      <section className="mb-6 p-4 rounded-2xl border border-white/10 bg-white/[0.02]">
        <h3 className="text-[15px] font-black text-white mb-2">Entretien de sensibilisation</h3>
        <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/25 text-[12.5px] text-white/85 mb-3">
          <strong className="text-rose-300">Questions :</strong> Que fais-tu si un ami te propose de rouler après avoir bu ? Comment refuses-tu ? Quel est ton plan B ?
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-1 block">Engagement personnel</label>
            <textarea value={state.engagement || ''} onChange={(e) => save({ engagement: e.target.value })} rows={4}
              placeholder="Je m'engage à…" className="w-full rounded-lg bg-black/40 border border-white/15 focus:border-rose-500 outline-none px-3 py-2 text-white text-[13px]"
              data-testid="bilan-engagement" />
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-1 block">Observation enseignant</label>
            <textarea value={state.observation || ''} onChange={(e) => save({ observation: e.target.value })} rows={4}
              placeholder="Points forts · axes de progrès · recommandations…" className="w-full rounded-lg bg-black/40 border border-white/15 focus:border-rose-500 outline-none px-3 py-2 text-white text-[13px]"
              data-testid="bilan-observation" />
          </div>
        </div>
      </section>

      {/* V8 · Trace de progression (récap des 4 séances) */}
      <section className="mb-6 p-5 rounded-2xl border-2 border-rose-500/25 bg-gradient-to-br from-rose-500/[0.06] via-white/[0.02] to-transparent" data-testid="trace-progression">
        <div className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-1">V8 · Trace de progression</div>
        <h3 className="text-lg font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Récapitulatif des 4 séances</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-left text-white/60 text-[10px] font-black uppercase tracking-widest border-b border-white/10">
                <th className="py-2 pr-3 w-24">Séance</th>
                <th className="py-2 pr-3">Réussite principale</th>
                <th className="py-2 pr-3">Difficulté principale</th>
                <th className="py-2 pr-3">Correction retenue</th>
                <th className="py-2">Objectif suivant</th>
              </tr>
            </thead>
            <tbody>
              {['s1','s2','s3','s4'].map((sid, i) => {
                const niv = NIVEAUX_ACQUISITION.find((n) => n.key === state[`niv-${sid}`]);
                return (
                  <tr key={sid} className="border-b border-white/5 align-top">
                    <td className="py-2 pr-3">
                      <div className="text-white/90 font-black text-[12.5px]">Séance {i+1}</div>
                      {niv && <div className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-full text-[9.5px] font-black uppercase tracking-widest" style={{ background: `${niv.color}22`, color: niv.color, border: `1px solid ${niv.color}55` }}>{niv.emoji} {niv.label}</div>}
                    </td>
                    <td className="py-2 pr-3 text-white/80 text-[12px]">{state[`trace-reussite-${sid}`] || <em className="text-white/35">—</em>}</td>
                    <td className="py-2 pr-3 text-white/80 text-[12px]">{state[`trace-difficulte-${sid}`] || <em className="text-white/35">—</em>}</td>
                    <td className="py-2 pr-3 text-white/80 text-[12px]">{state[`trace-correction-${sid}`] || <em className="text-white/35">—</em>}</td>
                    <td className="py-2 text-white/80 text-[12px]">{state[`trace-objectif-${sid}`] || <em className="text-white/35">—</em>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-white/50 mt-2 italic">💡 Les colonnes se remplissent automatiquement depuis la trace pédagogique de chaque séance.</p>
      </section>

      {/* V8 · Évaluation pédagogique finale */}
      <section className="mb-6 p-5 rounded-2xl border-2 border-amber-500/25 bg-gradient-to-br from-amber-500/[0.06] via-white/[0.02] to-transparent" data-testid="eval-finale">
        <div className="text-[10px] font-black uppercase tracking-widest text-amber-400 mb-1">V8 · Évaluation pédagogique finale</div>
        <h3 className="text-lg font-black text-white mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Grille de synthèse — 8 compétences</h3>
        <p className="text-[12px] text-white/60 mb-3 leading-relaxed">Observer une compétence, pas seulement une performance. L'élève doit pouvoir <strong className="text-amber-300">réussir</strong>, <strong className="text-amber-300">expliquer</strong> et <strong className="text-amber-300">transférer</strong>.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-left text-white/60 text-[10px] font-black uppercase tracking-widest border-b border-white/10">
                <th className="py-2 pr-3">Compétence</th>
                {EVAL_FINALE_COLS.map((c) => (
                  <th key={c.key} className="py-2 pr-3 text-center w-24" title={c.hint}>{c.label}</th>
                ))}
                <th className="py-2 text-center w-32">Statut</th>
              </tr>
            </thead>
            <tbody>
              {EVAL_FINALE_GRILLE.map((row, i) => {
                const statutKey = `evalf-${i}`;
                const statut = state[statutKey];
                return (
                  <tr key={i} className="border-b border-white/5 align-top">
                    <td className="py-2 pr-3">
                      <div className="text-white/90 font-black text-[12.5px]">{row.competence}</div>
                      <div className="text-white/50 text-[11px]">{row.desc}</div>
                    </td>
                    {EVAL_FINALE_COLS.map((c) => {
                      const k = `evalf-${i}-${c.key}`;
                      const on = Boolean(state[k]);
                      return (
                        <td key={c.key} className="py-2 pr-3 text-center">
                          <button type="button" onClick={() => save({ [k]: !on })}
                            className={`w-7 h-7 rounded-lg border-2 inline-flex items-center justify-center transition ${on ? 'bg-emerald-500 border-emerald-500' : 'border-white/20 hover:border-white/40 bg-transparent'}`}
                            data-testid={`evalf-${i}-${c.key}`}>
                            {on && <CheckCircle2 className="w-4 h-4 text-white" />}
                          </button>
                        </td>
                      );
                    })}
                    <td className="py-2 text-center">
                      <div className="inline-flex gap-1">
                        {[['🟢','vert','#10b981'],['🟠','orange','#f59e0b'],['🔴','rouge','#e11d48']].map(([emoji, key, c]) => (
                          <button key={key} onClick={() => save({ [statutKey]: statut === key ? null : key })}
                            className="w-7 h-7 rounded-full border-2 text-[12px] transition"
                            style={{ borderColor: statut === key ? c : `${c}44`, background: statut === key ? `${c}33` : 'transparent' }}
                            data-testid={`evalf-statut-${i}-${key}`}>{emoji}</button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>


      <section className="p-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.03]">
        <h3 className="text-[15px] font-black text-white mb-3">Validation finale</h3>
        {[
          "Toutes les compétences officielles ont été abordées",
          "L'élève a verbalisé les risques et les préventions",
          "Le bilan pédagogique a été co-signé (élève + enseignant)",
        ].map((label, i) => (
          <label key={i} className="flex items-start gap-2 py-1 text-[13px] text-white/85 cursor-pointer">
            <input type="checkbox" checked={Boolean(state[`vf-${i}`])} onChange={() => toggle(`vf-${i}`)} className="mt-0.5 w-4 h-4 accent-emerald-500 cursor-pointer" data-testid={`vf-${i}`} />
            <span>{label}</span>
          </label>
        ))}
        <Button onClick={() => window.print()} className="mt-4 bg-emerald-500 hover:bg-emerald-400 text-white font-black uppercase tracking-widest" data-testid="btn-print-pdf">
          <Printer className="w-4 h-4 mr-2" /> Imprimer / Enregistrer en PDF
        </Button>
      </section>
    </>
  );
}
